import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Gift, ChevronRight, ChevronDown } from 'lucide-react';
const bonuses = [{
  title: 'Bono 1 — Directorio de Interacciones Medicamentosas Críticas',
  description: 'Checklist visual y de consulta rápida con las combinaciones farmacológicas de mayor riesgo en psiquiatría. Una herramienta que tenés a mano en cada prescripción para tomar decisiones seguras sin perder tiempo buscando en otras fuentes.'
}, {
  title: 'Bono 2 — Guía de Psicoeducación para Familiares',
  description: 'PDF listo para entregar al paciente y su familia. Explicaciones claras sobre los diagnósticos más frecuentes, la importancia de la adherencia al tratamiento y cómo acompañar desde el entorno. Reducís consultas innecesarias y mejorás los resultados terapéuticos.'
}, {
  title: 'Bono 3 — Planilla de Screening de Sustancias y Adicciones',
  description: 'Evaluación rápida y estructurada del consumo de sustancias integrada a la entrevista inicial. Compatible con criterios DSM-5/CIE-11. Detectás comorbilidades desde la primera sesión sin extender el tiempo de consulta.'
}, {
  title: 'Bono 4 — Protocolo Anti-Burnout para Psiquiatras',
  description: 'Una herramienta de autocuidado profesional diseñada para quienes cargan con el peso emocional de los casos más complejos. Indicadores de alerta, estrategias de regulación y registro personal de bienestar clínico.'
}, {
  title: 'Bono 5 — Script de Cobro y Gestión de Pacientes Morosos',
  description: 'Exactamente qué decir y cómo decirlo para proteger tus honorarios sin dañar el vínculo terapéutico. Incluye modelos de mensaje para WhatsApp, políticas de cancelación y protocolo de baja por deuda.'
}, {
  title: 'Bono 6 — Pack de 20 Plantillas de Instagram para Psiquiatras',
  description: 'Plantillas editables en Canva para publicar contenido de autoridad en redes sociales. Diseñadas para posicionarte como referente en salud mental y atraer pacientes ideales sin necesidad de saber de diseño ni de marketing.'
}];
export function BonusSection() {
  const [expandedBonus, setExpandedBonus] = useState<number | null>(null);
  return <section className="py-12 md:py-24 bg-navy-light relative border-y border-gold/20" data-id="element-12">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-13">
        <div className="text-center mb-8 md:mb-16" data-id="element-14">
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} className="inline-flex items-center justify-center space-x-2 bg-red-600 text-white px-4 py-1.5 md:px-6 md:py-2 rounded-full font-bold tracking-wide uppercase mb-4 md:mb-6 shadow-[0_0_15px_rgba(220,38,38,0.5)] text-sm md:text-base" data-id="element-15">
            <Gift className="w-4 h-4 md:w-5 md:h-5" data-id="element-16" />
            <span data-id="element-17">GRATIS POR EL DÍA DE HOY</span>
          </motion.div>

          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-2xl md:text-5xl font-serif font-bold text-white mb-2 md:mb-4" data-id="element-18">
            Bonos Exclusivos
          </motion.h2>
        </div>

        {/* Mobile: Compact accordion */}
        <div className="flex flex-col gap-2 md:hidden" data-id="element-19">
          {bonuses.map((bonus, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 15
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.3,
          delay: index * 0.05
        }} className="bg-navy border border-gold/20 rounded-lg overflow-hidden" data-id="element-20">
              <button onClick={() => setExpandedBonus(expandedBonus === index ? null : index)} className="w-full flex items-center gap-3 p-3 text-left" data-id="element-21">
                <span className="text-xl shrink-0" data-id="element-22">🎁</span>
                <h3 className="text-sm font-bold text-gold leading-snug flex-grow" data-id="element-23">
                  {bonus.title}
                </h3>
                <ChevronDown className={`w-4 h-4 text-gold shrink-0 transition-transform duration-300 ${expandedBonus === index ? 'rotate-180' : ''}`} data-id="element-24" />
              </button>

              <AnimatePresence data-id="element-25">
                {expandedBonus === index && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.25
            }} className="overflow-hidden" data-id="element-26">
                    <p className="text-gray-300 text-xs leading-relaxed px-3 pb-3 border-t border-white/5 pt-2" data-id="element-27">
                      {bonus.description}
                    </p>
                  </motion.div>}
              </AnimatePresence>
            </motion.div>)}
        </div>

        {/* Desktop: Grid layout */}
        <div className="hidden md:grid md:grid-cols-2 gap-6" data-id="element-28">
          {bonuses.map((bonus, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: index * 0.1
        }} className="bg-navy border border-gold/20 rounded-xl p-6 hover:border-gold/50 transition-colors group relative overflow-hidden" data-id="element-29">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gold/5 rounded-bl-full -z-10 group-hover:bg-gold/10 transition-colors" data-id="element-30"></div>
              <div className="flex items-start space-x-4" data-id="element-31">
                <div className="text-3xl mt-1" data-id="element-32">🎁</div>
                <div data-id="element-33">
                  <h3 className="text-lg font-bold text-gold mb-2 leading-tight" data-id="element-34">
                    {bonus.title}
                  </h3>
                  <p className="text-gray-300 text-sm leading-relaxed" data-id="element-35">
                    {bonus.description}
                  </p>
                </div>
              </div>
            </motion.div>)}
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="mt-8 md:mt-16 text-center glass-panel rounded-xl md:rounded-2xl p-5 md:p-8 max-w-2xl mx-auto border-gold/30" data-id="element-36">
          <p className="text-base md:text-xl text-gray-400 mb-1 md:mb-2" data-id="element-37">
            Valor total de los 6 bonos:
          </p>
          <p className="text-2xl md:text-3xl font-bold text-gray-500 line-through decoration-red-500 mb-2 md:mb-4" data-id="element-38">
            $84.000 ARS
          </p>
          <p className="text-2xl md:text-4xl font-serif font-bold text-gold mb-5 md:mb-8" data-id="element-39">
            Por el día de hoy → GRATIS ✅
          </p>

          <a href="Link de tu checkout" className="group relative inline-flex items-center justify-center w-full sm:w-auto px-6 py-3 md:px-8 md:py-4 text-base md:text-lg font-bold text-navy bg-gold rounded-lg overflow-hidden transition-all hover:scale-105 hover:shadow-[0_0_20px_rgba(201,168,76,0.4)]" data-id="element-40">
            <span className="relative z-10 flex items-center" data-id="element-41">
              👉 Acceder al Sistema PRO ahora
              <ChevronRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-42" />
            </span>
            <div className="absolute inset-0 h-full w-full bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite]" data-id="element-43"></div>
          </a>
        </motion.div>
      </div>
    </section>;
}